import {ReactElement, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {styled} from "styled-components";

import {cloturerContrat, reouvrirContrat} from "@/Api/contrat";
import IconLock from "@/assets/iconLock.svg?react";
import IconPrevious from "@/assets/iconPrevious.svg?react";
import IconUnlock from "@/assets/iconUnlock.svg?react";
import {Button} from "@/components/Button/Button";
import {BlocSuiviContrat} from "@/components/Contrat/BlocSuiviContrat";
import {ErrorBoundary} from "@/components/ErrorBoundary/ErrorBoundary";
import {FournisseurContentNoContrat} from "@/components/Fournisseur/FournisseurContent/FournisseurContentNoContrat";
import {FournisseurHeader} from "@/components/Fournisseur/FournisseurHeader/FournisseurHeader";
import {LoadingOrError} from "@/components/LoadingOrError/LoadingOrError";
import {AppContext} from "@/context/AppContext";
import {
  FournisseurContext,
  FournisseurContextType,
} from "@/context/FournisseurContext";
import {HookFormContextProvider} from "@/context/HookFormContext";
import {
  UserParamContext,
  UserParamContextType,
} from "@/context/UserParamContext";
import {useGetContext} from "@/hooks/useGetContext/useGetContext";
import {useIsConnectedTest} from "@/hooks/useIsConnectedTest/useIsConnectedTest";
import {AppContext as AppContextType} from "@/ts/appContext";

const FournisseurContent = styled.div`
  width: 100%;
  display: grid;
  gap: 40px;
  margin-top: 15px;
`;

const ButtonCloturerContrat = styled(Button)<{isCloture: boolean}>`
  margin: 15px 0;
  text-align: center;
  justify-content: center;
  border: 2px solid white;
  background-color: ${({isCloture, theme}) =>
    isCloture ? theme.colors.mediumSecondary : theme.colors.alert};
  outline: 2px solid
    ${({isCloture, theme}) =>
      isCloture ? theme.colors.mediumSecondary : theme.colors.alert};
  & span {
    font-size: 1rem;
  }
`;

// page Fournisseur qui affiche les informations du fournisseur et le contrat entre le fournisseur et l'adherent
export function Fournisseur(): ReactElement | null {
  const navigate = useNavigate();
  const {
    fournisseurAndCampagne,
    getFournisseur,
    fournisseurAndCampagneLoadingState,
  } = useGetContext<FournisseurContextType>(
    FournisseurContext,
    "FournisseurContext"
  );
  const {API} = useGetContext<AppContextType>(AppContext, "AppContext");
  const {currentCampagne, currentContremarque} =
    useGetContext<UserParamContextType>(UserParamContext, "UserParamContext");

  const [savedCampagneAndContremarque, setSavedCampagneAndContremarque] =
    useState<{campagne: string; contremarque: string}>({
      campagne: "",
      contremarque: "",
    });

  const isAuthorized = useIsConnectedTest();

  useEffect(() => {
    if (
      currentCampagne.value !== savedCampagneAndContremarque.campagne ||
      currentContremarque.value !== savedCampagneAndContremarque.contremarque
    ) {
      getFournisseur();
      setSavedCampagneAndContremarque({
        campagne: currentCampagne.value,
        contremarque: currentContremarque.value,
      });
    }
  }, [currentCampagne, currentContremarque]);

  // si l'utilisateur n'est pas connecté, on ne rend pas la page
  if (!isAuthorized) {
    return null;
  }

  return (
    <ErrorBoundary
      fallbackText="Erreur lors de la récupération des données du fournisseur"
      buttonToRefresh={true}>
      <Button label="RETOUR" icon={IconPrevious} onClick={() => navigate(-1)} />
      <FournisseurContent data-testid="FournisseurContent">
        <LoadingOrError
          loadingState={fournisseurAndCampagneLoadingState}
          errorImage="/path/to/error-image.png"
          buttonToRefresh={true}
          onRetry={async () => await getFournisseur()}
          data={fournisseurAndCampagne}
          dataValidator={data => data && data?.campagne && data?.fournisseur}>
          {/* On affiche le contenu si le fournisseur est trouvé */}
          {fournisseurAndCampagne && (
            <>
              {["encours"].includes(
                fournisseurAndCampagne?.contrat?.statut || ""
              ) ||
              fournisseurAndCampagne?.contrat === null ||
              fournisseurAndCampagne?.contrat === undefined ? (
                <HookFormContextProvider>
                  <FournisseurContentNoContrat />
                </HookFormContextProvider>
              ) : fournisseurAndCampagne?.contrat?.statut == "actif" ||
                fournisseurAndCampagne?.contrat?.statut === "cloture" ? (
                <HookFormContextProvider>
                  <FournisseurHeader
                    fournisseurInfo={fournisseurAndCampagne?.fournisseur}
                    bonus={fournisseurAndCampagne?.campagne?.bonus}
                    contratEnCours={["actif", "cloture", "encours"].includes(
                      fournisseurAndCampagne?.contrat?.statut
                    )}></FournisseurHeader>
                  {new Date(fournisseurAndCampagne?.campagne?.fin) <
                  new Date() ? (
                    <ButtonCloturerContrat
                      isCloture={
                        fournisseurAndCampagne?.contrat?.statut === "cloture"
                      }
                      label={
                        fournisseurAndCampagne?.contrat?.statut === "actif"
                          ? "Cloturer le contrat"
                          : "Réouvrir le contrat"
                      }
                      variant="secondary"
                      icon={
                        fournisseurAndCampagne?.contrat?.statut === "actif"
                          ? IconLock
                          : IconUnlock
                      }
                      onClick={async () => {
                        if (
                          fournisseurAndCampagne?.contrat?.statut === "actif"
                        ) {
                          await cloturerContrat(
                            API,
                            fournisseurAndCampagne?.contrat?.contrat_id
                          );
                          getFournisseur();
                        } else {
                          if (fournisseurAndCampagne?.contrat?.contrat_id) {
                            await reouvrirContrat(
                              API,
                              fournisseurAndCampagne?.contrat?.contrat_id
                            );
                            getFournisseur();
                          }
                        }
                      }}
                    />
                  ) : null}
                  <BlocSuiviContrat contrat={fournisseurAndCampagne?.contrat} />
                </HookFormContextProvider>
              ) : null}
            </>
          )}
        </LoadingOrError>
      </FournisseurContent>
    </ErrorBoundary>
  );
}
